import { useCallback, useEffect, useState } from 'react';

import {
  getPowerHealth,
  getThermalState,
  subscribePowerHealth,
  type DevicePowerHealth,
  type ThermalState,
} from 'lib/medsi/device-health';

export function useDeviceHealthSensors(): {
  thermalState: ThermalState;
  lowPowerMode: boolean;
  batteryLevel: number | null;
  refresh: () => Promise<{
    thermalState: ThermalState;
    power: DevicePowerHealth;
  }>;
} {
  const [thermalState, setThermalState] = useState<ThermalState>('unknown');
  const [lowPowerMode, setLowPowerMode] = useState(false);
  const [batteryLevel, setBatteryLevel] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    const [nextThermal, nextPower] = await Promise.all([
      getThermalState(),
      getPowerHealth(),
    ]);
    setThermalState(nextThermal);
    setLowPowerMode(nextPower.lowPowerMode);
    setBatteryLevel(nextPower.batteryLevel);
    return { thermalState: nextThermal, power: nextPower };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void refresh();

    const powerSub = subscribePowerHealth(health => {
      if (!cancelled) {
        setLowPowerMode(health.lowPowerMode);
        setBatteryLevel(health.batteryLevel);
      }
    });

    return () => {
      cancelled = true;
      powerSub.remove();
    };
  }, [refresh]);

  return { thermalState, lowPowerMode, batteryLevel, refresh };
}
